import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import LoadIndicator from 'devextreme-react/load-indicator';
import {
  FileReviewWorkspace,
  type FileReviewPanelPlacement,
  enableFileReviewDiscussionPlacement,
  readFileReviewPanelOpen,
  useAutoHideDiscussionPanel,
  useDiscussionAnchorFromUrl,
  useFileReviewPanelPlacement,
  useFileReviewPanelWidth,
  writeFileReviewPanelOpen,
  writeFileReviewPanelPlacement
} from '@jarvis/platform-file-review';
import '@jarvis/platform-file-review/styles.css';
import { shouldShowDocumentViewOnlyBadge } from '../documentEditor/documentEditorCollaboration';
import { bootstrapFileReviewApi } from '../fileReview/bootstrapFileReviewApi';
import {
  formatMediaDateModified,
  formatMediaFileSize
} from '../mediaViewer/mediaViewerFormatters';
import { useMediaFileMetadata } from '../mediaViewer/mediaViewerMetadata';
import { DiscussionNotificationPrompt } from '../notifications/DiscussionNotificationPrompt';
import { showToast } from '../notifications/toast';
import { useDiscussionMessageNotifications } from '../notifications/useDiscussionMessageNotifications';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { useDocumentPresence } from '../realtime/useDocumentPresence';
import { useShellLayout } from '../shell/useShellLayout';
import { isFileReviewDisabledStorageMount } from '../storageFileAudit/formatters';
import { isFileAccessRestrictedStatus } from '../storageAccess/fileAccessRestrictedUrls';
import { JarvisPdfViewer, type JarvisPdfViewerHandle } from './JarvisPdfViewer';
import { PdfPreviewRibbon } from './PdfPreviewRibbon';
import { pdfPreviewSrc } from './pdfPreviewUrls';
import { pdfCommentSelectionMessage } from './pdfSelection';
import { usePdfReviewThread } from './usePdfReviewThread';
import { usePdfViewerIdentity } from './usePdfViewerIdentity';
import { pdfAnchorsFromThread } from './pdfViewerTypes';
import { readPdfHighlightsVisible, writePdfHighlightsVisible } from './pdfViewerUiPrefs';
import './PdfViewerSurface.css';

bootstrapFileReviewApi();

export interface PdfViewerSurfaceProps {
  mountId: string;
  path: string;
  enabled?: boolean;
  discussionPlacement?: FileReviewPanelPlacement;
  discussionAnchorId?: string | null;
  onRestrictedAccess: () => void;
  renderHeader?: (state: PdfViewerSurfaceHeaderState) => ReactNode;
  className?: string;
}

export interface PdfViewerSurfaceHeaderState {
  placement: FileReviewPanelPlacement;
  discussionExpanded: boolean;
  onDiscussionToggle?: () => void;
}

export function PdfViewerSurface({
  mountId,
  path,
  enabled = true,
  discussionPlacement: discussionPlacementProp,
  discussionAnchorId = null,
  onRestrictedAccess,
  renderHeader,
  className = ''
}: PdfViewerSurfaceProps) {
  const hasSource = enabled && Boolean(mountId && path);
  const { isNarrow } = useShellLayout();
  const discussionsEnabled = !isFileReviewDisabledStorageMount(mountId) && !isNarrow;
  const storedPlacement = useFileReviewPanelPlacement('pdf');
  const placement = discussionsEnabled
    ? (discussionPlacementProp ?? storedPlacement)
    : 'off';
  const autoHideDiscussion = useAutoHideDiscussionPanel();
  const workspaceRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const pdfViewerRef = useRef<JarvisPdfViewerHandle>(null);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadErrorDetail, setLoadErrorDetail] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(() => readFileReviewPanelOpen('pdf'));
  const [showOverlays, setShowOverlays] = useState(readPdfHighlightsVisible);
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null);
  const [jumpRequest, setJumpRequest] = useState(0);

  const fileUrl = useMemo(
    () => (hasSource ? pdfPreviewSrc(mountId, path) : ''),
    [hasSource, mountId, path]
  );
  const { panelStyle, startResize, resetWidth } = useFileReviewPanelWidth(
    workspaceRef,
    placement,
    'pdf'
  );

  useEffect(() => {
    if (!fileUrl) return;
    setLoadState('loading');
    setLoadErrorDetail(null);
  }, [fileUrl]);

  useEffect(() => {
    setActiveAnchorId(null);
  }, [mountId, path, enabled]);

  const openDiscussionPanel = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') enableFileReviewDiscussionPlacement('pdf');
    setChatOpen(true);
    writeFileReviewPanelOpen(true, 'pdf');
  }, [discussionsEnabled, placement]);

  const metadata = useMediaFileMetadata(mountId, path, hasSource);
  const { userId, userName, hasPhoto, userAliases } = usePdfViewerIdentity(hasSource);
  const { roster } = useDocumentPresence({ mountId, path, enabled: hasSource });
  const { onPeerActivity } = useDiscussionMessageNotifications({
    enabled: discussionsEnabled && hasSource && Boolean(userId),
    onOpenDiscussionPanel: openDiscussionPanel
  });

  const requestJumpToAnchor = useCallback((anchorId: string | null) => {
    setActiveAnchorId(anchorId);
    if (anchorId) setJumpRequest((value) => value + 1);
  }, []);

  useDiscussionAnchorFromUrl({
    discussionAnchorId: discussionsEnabled ? discussionAnchorId : null,
    onAnchorFromUrl: requestJumpToAnchor,
    onOpenDiscussion: openDiscussionPanel
  });

  const onReviewError = useCallback((message: string) => showToast(message, 'error'), []);
  const review = usePdfReviewThread(mountId, path, hasSource && discussionsEnabled, {
    commentsPanelOpen: discussionsEnabled && placement !== 'off',
    onError: onReviewError,
    currentUserId: userId,
    currentUserAliases: userAliases,
    onPeerActivity
  });
  const annotateReadOnly = !discussionsEnabled || (!review.loading && !review.canAnnotate);
  const showViewOnlyBadge = shouldShowDocumentViewOnlyBadge({
    discussionsEnabled,
    annotateReadOnly,
    hasReviewError: Boolean(review.error)
  });

  const toggleChat = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') {
      enableFileReviewDiscussionPlacement('pdf');
      setChatOpen(true);
      writeFileReviewPanelOpen(true, 'pdf');
      return;
    }
    setChatOpen((current) => {
      const next = !current;
      writeFileReviewPanelOpen(next, 'pdf');
      return next;
    });
  }, [discussionsEnabled, placement]);

  const onDiscussionPlacementChange = useCallback(
    (next: FileReviewPanelPlacement) => {
      if (!discussionsEnabled) return;
      const panelOpen = next !== 'off';
      writeFileReviewPanelPlacement(next, 'pdf');
      writeFileReviewPanelOpen(panelOpen, 'pdf');
      setChatOpen(panelOpen);
      if (next === 'off') setActiveAnchorId(null);
    },
    [discussionsEnabled]
  );

  const toggleOverlays = useCallback(() => {
    setShowOverlays((visible) => {
      const next = !visible;
      writePdfHighlightsVisible(next);
      return next;
    });
  }, []);

  const commentOnSelection = useCallback(() => {
    const result = pdfViewerRef.current?.commentOnSelection() ?? 'no-selection';
    const message = pdfCommentSelectionMessage(result);
    if (message) showToast(message, 'warning');
  }, []);

  const onHighlightCommitted = useCallback(
    async (payload: {
      areas: import('./pdfViewerTypes').PdfHighlightArea[];
      selectedText: string;
      comment: string;
    }) => {
      const anchorId = await review.commitHighlight(payload);
      if (anchorId) requestJumpToAnchor(anchorId);
    },
    [review, requestJumpToAnchor]
  );

  const onMessageEntered = useCallback(
    async (text: string) => {
      await review.sendMessage(text, null);
    },
    [review]
  );

  const onMessageDeleted = useCallback(
    async (messageId: string) => {
      const deletedAnchorId = review.messages.find((message) => message.id === messageId)?.anchorId ?? null;
      await review.deleteMessage(messageId);
      if (deletedAnchorId) {
        setActiveAnchorId((current) => (current === deletedAnchorId ? null : current));
      }
    },
    [review]
  );

  const viewerHighlights = showOverlays ? review.highlights : [];
  const pdfAnchors = pdfAnchorsFromThread(review.thread);
  const actionsDisabled = loadState !== 'loaded';

  return (
    <div className={`pdf-viewer-surface ${className}`.trim()}>
      {renderHeader?.({
        placement,
        discussionExpanded: chatOpen,
        onDiscussionToggle: discussionsEnabled ? toggleChat : undefined
      })}
      <div className="pdf-viewer-surface__status">
        <span className="pdf-viewer-surface__presence">
          <ActiveUsersBar roster={roster} currentUserId={userId} maxAvatars={5} />
        </span>
        <span className="media-viewer-chrome__meta pdf-viewer-surface__meta" aria-label="File details">
          <span className="media-viewer-chrome__meta-item">
            <span className="media-viewer-chrome__meta-label">Modified</span>
            <span className="media-viewer-chrome__meta-value">
              {formatMediaDateModified(metadata?.dateModified)}
            </span>
          </span>
          <span className="media-viewer-chrome__meta-sep" aria-hidden="true">·</span>
          <span className="media-viewer-chrome__meta-item">
            <span className="media-viewer-chrome__meta-label">Size</span>
            <span className="media-viewer-chrome__meta-value">
              {formatMediaFileSize(metadata?.size)}
            </span>
          </span>
        </span>
        {showViewOnlyBadge ? <span className="pdf-viewer-surface__badge">View only</span> : null}
        {discussionsEnabled && review.error && review.messages.length === 0 ? (
          <span className="pdf-viewer-surface__badge pdf-viewer-surface__badge--error" role="alert">
            Comments unavailable
          </span>
        ) : null}
      </div>
      <PdfPreviewRibbon
        mountId={mountId}
        relativePath={path}
        disabled={actionsDisabled}
        canCommentOnSelection={!annotateReadOnly}
        discussionsEnabled={discussionsEnabled}
        chatOpen={chatOpen}
        onToggleChat={toggleChat}
        showOverlays={showOverlays}
        onToggleOverlays={toggleOverlays}
        onCommentOnSelection={commentOnSelection}
        placement={placement}
        onDiscussionPlacementChange={onDiscussionPlacementChange}
        fullscreenTargetRef={stageRef}
      />
      <div ref={stageRef} className="pdf-viewer-surface__stage">
        <FileReviewWorkspace
          workspaceRef={workspaceRef}
          placement={placement}
          panelOpen={discussionsEnabled && chatOpen}
          autoHide={autoHideDiscussion}
          viewerClassName="pdf-viewer-surface__pdf-pane jarvis-pdf-viewer-stage"
          panelProps={{
            messages: review.messages,
            currentUserId: userId,
            currentUserAliases: userAliases,
            currentUserName: userName,
            currentUserHasPhoto: hasPhoto,
            loading: review.loading,
            readOnly: annotateReadOnly,
            canAnnotate: review.canAnnotate,
            activeAnchorId,
            jumpLabel: 'Jump to highlight',
            title: 'Discussion',
            panelStyle: chatOpen ? panelStyle : undefined,
            onResizeStart: startResize,
            onResizeReset: resetWidth,
            onMessageEntered,
            onMessageUpdated: review.updateMessage,
            onMessageDeleted,
            onMessageClick: requestJumpToAnchor,
            onComposeValidationError: onReviewError,
            mountId,
            path
          }}
        >
          {loadState === 'loading' ? (
            <div className="media-viewer-stage__loading" aria-busy="true" aria-live="polite">
              <LoadIndicator visible height={28} width={28} />
              <span>Loading PDF…</span>
            </div>
          ) : null}
          {loadState === 'error' ? (
            <p className="media-viewer-stage__error">
              {loadErrorDetail ?? 'Could not load this PDF. Check permissions or try again.'}
            </p>
          ) : null}
          {fileUrl ? (
            <JarvisPdfViewer
              ref={pdfViewerRef}
              fileUrl={fileUrl}
              mountId={mountId}
              path={path}
              readOnly={annotateReadOnly}
              highlights={viewerHighlights}
              anchors={pdfAnchors}
              activeAnchorId={activeAnchorId}
              jumpRequest={jumpRequest}
              onHighlightCommitted={onHighlightCommitted}
              onHighlightClick={requestJumpToAnchor}
              onCommentValidationError={onReviewError}
              onDocumentLoad={() => setLoadState('loaded')}
              onDocumentError={(detail, status) => {
                if (status != null && isFileAccessRestrictedStatus(status)) {
                  onRestrictedAccess();
                  return;
                }
                setLoadState('error');
                setLoadErrorDetail(detail ?? null);
              }}
            />
          ) : null}
        </FileReviewWorkspace>
      </div>
      <DiscussionNotificationPrompt enabled={discussionsEnabled && hasSource} />
    </div>
  );
}
